import { useState, type FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ErreurApi, api } from '../lib/apiClient';
import { Champ, Modale } from './Elements';

/**
 * Changement de mot de passe en libre-service.
 *
 * Le backend revoque toutes les sessions de l'utilisateur au passage : si
 * la raison du changement est une compromission, les laisser ouvertes
 * viderait le geste de son sens. Une reconnexion est donc attendue, et
 * annoncee avant d'agir plutot que subie apres.
 */
export function ChangementMotDePasse({
  ouverte,
  onFermer,
}: {
  ouverte: boolean;
  onFermer: () => void;
}) {
  const { deconnecter } = useAuth();
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);

    if (nouveau !== confirmation) {
      setErreur('Les deux saisies du nouveau mot de passe different.');
      return;
    }

    if (nouveau.length < 12) {
      setErreur('Le nouveau mot de passe doit comporter au moins 12 caracteres.');
      return;
    }

    setEnCours(true);
    try {
      await api.post('/api/auth/mot-de-passe', {
        motDePasseActuel: actuel,
        nouveauMotDePasse: nouveau,
      });
      // La session vient d'etre revoquee cote serveur : on aligne l'etat
      // du client plutot que de le laisser croire qu'il est connecte.
      await deconnecter();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Changement impossible.');
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Changer mon mot de passe" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        <Champ libelle="Mot de passe actuel">
          <input
            className="champ"
            type="password"
            value={actuel}
            onChange={(e) => setActuel(e.target.value)}
            autoComplete="current-password"
            required
            autoFocus
          />
        </Champ>

        <Champ libelle="Nouveau mot de passe" aide="12 caracteres minimum.">
          <input
            className="champ"
            type="password"
            value={nouveau}
            onChange={(e) => setNouveau(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
          />
        </Champ>

        <Champ libelle="Confirmer le nouveau mot de passe">
          <input
            className="champ"
            type="password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Champ>

        {erreur && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
            {erreur}
          </p>
        )}

        <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
          Toutes vos sessions seront fermees : vous devrez vous reconnecter avec le nouveau mot de
          passe.
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="bouton-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button type="submit" className="bouton-principal" disabled={enCours}>
            {enCours ? 'Changement…' : 'Changer le mot de passe'}
          </button>
        </div>
      </form>
    </Modale>
  );
}
